import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import {
  clientFamilyBackgroundActions,
  educationBackgroundActions,
  treatmentPlanActions,
} from '../../../../../store';
import { getAllInvalidFieldNames, validateSync } from '../../../../../helpers';
import {
  EducationBackgroundSchema,
  educationBackgroundInitialState,
  getParentCaregiverSchema,
  siblingSchema,
  ParentCaregiverListSchema,
  EMAIL_TEST_REGEX,
  FamilyBackgroundSchema,
} from '../../background/validationSchema';
import { TreatmentPlanPages, TreatmentPlanResourceVersion } from '../../constants/general';
import { MemberLanguages, ParentCaregiverRoles } from '../../background/constants';

import type { RootState } from '../../../../../store';
import type {
  IClientFamilyBackground,
  IEducationBackground,
  IParentCaregiver,
  ISibling,
} from '../../../../../utils/types';

function useValidateBackgroundPage() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const backgroundVersion = treatmentPlan?.resource_versions?.client_and_family_background;

  const { editingEducationBackground, treatmentPlanId: currentTPId } = useSelector(
    (state: RootState) => state.educationBackgrounds,
  );
  const { clientFamilyBackground, parentsCaregivers, siblings, strengths, interests } = useSelector(
    (state: RootState) => state.clientFamilyBackgrounds,
  );
  const { selected: selectedClient } = useSelector((state: RootState) => state.clients);

  const planId = treatmentPlan?.id;

  const [treatmentPlanId, setTreatmentPlanId] = useState<number | null>(null);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [fetchComplete, setFetchComplete] = useState<boolean>(false);

  useEffect(() => {
    if (!planId) return;
    setTreatmentPlanId(planId);
  }, [planId]);

  useEffect(() => {
    if (!treatmentPlanId || !selectedClient?.id) return;
    if (currentTPId === treatmentPlanId) {
      const educationBackgroundData = editingEducationBackground || null;
      const clientFamilyBackgroundsData = clientFamilyBackground || null;

      fetchAndValidateData(educationBackgroundData, clientFamilyBackgroundsData, treatmentPlanId);
    } else {
      fetchAndValidateData(null, null, treatmentPlanId);
    }
    dispatch(
      educationBackgroundActions.updateState({ key: 'treatmentPlanId', data: treatmentPlanId }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlanId, selectedClient]);

  useEffect(() => {
    if (!treatmentPlanId || !fetchComplete) return;

    validate(
      editingEducationBackground,
      clientFamilyBackground,
      parentsCaregivers ?? [],
      siblings ?? [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editingEducationBackground,
    clientFamilyBackground,
    parentsCaregivers,
    siblings,
    treatmentPlanId,
    fetchComplete,
  ]);

  const fetchAndValidateData = async (
    educationBackgroundData: IEducationBackground | null,
    clientFamilyBackgroundsData: IClientFamilyBackground | null,
    treatmentPlanId: number,
  ) => {
    setFetchComplete(false);
    if (!educationBackgroundData?.id) {
      await dispatch(
        educationBackgroundActions.getEducationBackground({
          treatmentPlanId: treatmentPlanId,
          getAccessTokenSilently,
        }),
      );
    }

    if (!clientFamilyBackgroundsData?.id) {
      await dispatch(
        clientFamilyBackgroundActions.getClientFamilyBackground({
          treatmentPlanId: treatmentPlanId,
          getAccessTokenSilently,
        }),
      );
    }
    setFetchComplete(true);
  };

  const validate = (
    editingEducationBackground: IEducationBackground | null,
    clientFamilyBackgroundsData: IClientFamilyBackground | null,
    parentsCargiversData: Array<IParentCaregiver>,
    siblingsData: Array<ISibling>,
  ) => {
    let educationBackgroundsErrors: Record<string, boolean> = {};
    let errorCount = 0;
    if (!editingEducationBackground?.id) {
      educationBackgroundsErrors = getAllInvalidFieldNames(
        EducationBackgroundSchema,
        educationBackgroundInitialState,
      );
    } else {
      const data = { ...editingEducationBackground };
      educationBackgroundsErrors = getAllInvalidFieldNames(EducationBackgroundSchema, data);
    }
    if (!clientFamilyBackgroundsData?.id) {
      errorCount += 1;
    }

    if (clientFamilyBackgroundsData?.id) {
      const familyBackgroundData = {
        family_medical_history_maternal:
          clientFamilyBackgroundsData.family_medical_history_maternal,
        family_medical_history_paternal:
          clientFamilyBackgroundsData.family_medical_history_paternal,
        community_involvement_religious_affiliations:
          clientFamilyBackgroundsData.community_involvement_religious_affiliations,
        client_family_background_version: backgroundVersion,
      };

      const familyBackgroundErrors = getAllInvalidFieldNames(
        FamilyBackgroundSchema,
        familyBackgroundData,
      );
      errorCount += Object.keys(familyBackgroundErrors).length;

      if (
        clientFamilyBackgroundsData.member_language === null ||
        clientFamilyBackgroundsData.member_language === MemberLanguages.Other
      )
        errorCount += 1;
    }
    if (clientFamilyBackgroundsData?.id && parentsCargiversData) {
      let errors = 0;
      if (parentsCargiversData.length) {
        const firstParentCaregiver = parentsCargiversData.find(
          (pc) =>
            pc.role === ParentCaregiverRoles['Parent and caregiver'] ||
            pc.role === ParentCaregiverRoles['Parent (not a caregiver)'],
        );
        parentsCargiversData.forEach((parentsCargiverData) => {
          const isValid = validateSync(
            getParentCaregiverSchema(
              parentsCargiverData.id === firstParentCaregiver?.id &&
                !EMAIL_TEST_REGEX.test(firstParentCaregiver?.email ?? ''),
            ),
            parentsCargiverData,
          );
          errors += isValid ? 0 : 1;
        });
      }
      errorCount += parentsCargiversData.length === 0 ? 1 : errors;
    }

    if (clientFamilyBackgroundsData?.id && parentsCargiversData) {
      const isValid = validateSync(ParentCaregiverListSchema, parentsCargiversData);
      errorCount += isValid ? 0 : 1;
    }

    if (clientFamilyBackgroundsData?.id && siblingsData) {
      let errors = 0;
      if (siblingsData.length) {
        siblingsData.forEach((siblingData) => {
          const isValid = validateSync(siblingSchema, siblingData);
          errors += isValid ? 0 : 1;
        });
      }
      errorCount += errors;
    }
    if (backgroundVersion === TreatmentPlanResourceVersion.V2) {
      if (clientFamilyBackgroundsData?.id && strengths) {
        if (strengths.length === 0) {
          errorCount += 1;
        }
      }
      if (clientFamilyBackgroundsData?.id && interests) {
        if (interests.length === 0) {
          errorCount += 1;
        }
      }
    }

    errorCount += Object.keys(educationBackgroundsErrors).length;
    setIsValid(errorCount === 0 ? true : false);

    dispatch(
      treatmentPlanActions.updateBadge({
        [TreatmentPlanPages.Background]: errorCount,
      }),
    );
  };

  return isValid && fetchComplete;
}

export default useValidateBackgroundPage;
